import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CircleDollarSign, Filter, Megaphone, Package, Pencil, Plus, RefreshCw, Search, Trash2, ToggleLeft, ToggleRight, Copy, AlertCircle, Store } from 'lucide-react'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Modal from '@/components/ui/modal'
import { getProducts } from '@/lib/api/products'
import {
  type PromotionResponse,
  type PromotionStateFilter,
  promotionsApi,
  type PromotionFilters,
} from '@/services/promotionsApi'
import PromotionFormModal, { type PromotionFormPayload } from './promotions/PromotionFormModal'
import {
  buildPromotionValidationMessage,
  countActivePromotionFilters,
  formatPromotionDate,
  getPromotionDiscountLabel,
  getPromotionEmptyState,
  getPromotionLifecycleBadge,
  getPromotionLifecycleDescription,
  getPromotionStateOptions,
  getPromotionTargetLabel,
} from './promotions/promotionsUx'
import CatalogNavTabs from '@/components/catalog/CatalogNavTabs'

const DEFAULT_FILTERS: PromotionFilters = {
  state: 'all',
  search: '',
  productId: '',
  page: 1,
  limit: 8,
}

export default function PromotionsPage() {
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const seededProductId = searchParams.get('productId')?.trim() || ''
  const shouldAutoCreate = searchParams.get('create') === 'true' || searchParams.get('new') === '1'
  const [filters, setFilters] = useState<PromotionFilters>(() => ({
    ...DEFAULT_FILTERS,
    productId: seededProductId,
  }))
  const [isFormOpen, setIsFormOpen] = useState(() => shouldAutoCreate)
  const [selectedPromotion, setSelectedPromotion] = useState<PromotionResponse | null>(null)
  const [defaultProductId, setDefaultProductId] = useState<string | null>(seededProductId || null)
  const [promotionToDelete, setPromotionToDelete] = useState<PromotionResponse | null>(null)
  const lastOpenedIntentRef = useRef<string | null>(shouldAutoCreate ? `${seededProductId || 'all'}` : null)

  useEffect(() => {
    if (!seededProductId) return
    setFilters((current) => (current.productId === seededProductId ? current : { ...current, productId: seededProductId, page: 1 }))
  }, [seededProductId])

  useEffect(() => {
    if (!shouldAutoCreate) return
    const intentKey = `${seededProductId || 'all'}`
    if (lastOpenedIntentRef.current !== intentKey) {
      lastOpenedIntentRef.current = intentKey
      setSelectedPromotion(null)
      setDefaultProductId(seededProductId || null)
      setIsFormOpen(true)
    }
  }, [shouldAutoCreate, seededProductId])

  const promotionsQuery = useQuery({
    queryKey: ['promotions', filters],
    queryFn: () => promotionsApi.getPromotions(filters),
    staleTime: 20_000,
  })

  const productsQuery = useQuery({
    queryKey: ['promotions-products'],
    queryFn: () => getProducts(),
    staleTime: 60_000,
  })

  const saveMutation = useMutation({
    mutationFn: async (payload: PromotionFormPayload) => {
      if (selectedPromotion) {
        return promotionsApi.updatePromotion(selectedPromotion.id, payload)
      }

      return promotionsApi.createPromotion(payload)
    },
    onSuccess: async (promotion) => {
      await queryClient.invalidateQueries({ queryKey: ['promotions'] })
      toast.success(selectedPromotion ? `Promoción ${promotion.name} actualizada` : `Promoción ${promotion.name} creada`)
    },
    onError: (error: any) => {
      const details = error?.response?.data?.details
      const message = buildPromotionValidationMessage(details) || error?.response?.data?.error || error?.message || 'No se pudo guardar la promoción'
      toast.error(message)
    },
  })

  const toggleMutation = useMutation({
    mutationFn: async (promotion: PromotionResponse) => promotionsApi.togglePromotion(promotion.id),
    onSuccess: async (promotion) => {
      await queryClient.invalidateQueries({ queryKey: ['promotions'] })
      toast.success(promotion.active ? 'Promoción activada' : 'Promoción desactivada')
    },
    onError: () => {
      toast.error('No se pudo actualizar el estado de la promoción')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (promotion: PromotionResponse) => promotionsApi.deletePromotion(promotion.id),
    onSuccess: async (promotion) => {
      await queryClient.invalidateQueries({ queryKey: ['promotions'] })
      toast.success(promotion.active ? 'Promoción actualizada' : 'Promoción desactivada')
    },
    onError: () => {
      toast.error('No se pudo desactivar la promoción')
    },
  })

  const promotions = promotionsQuery.data?.promotions ?? []
  const pagination = promotionsQuery.data?.pagination ?? { page: 1, limit: filters.limit ?? 8, total: 0, totalPages: 1 }
  const products = productsQuery.data ?? []
  const productMap = useMemo(() => new Map(products.map((product) => [product.id, product])), [products])
  const activeFilteredProduct = filters.productId ? productMap.get(filters.productId) : null

  const hasActiveFilters = countActivePromotionFilters(filters) > 0
  const emptyState = getPromotionEmptyState(pagination.total, hasActiveFilters, {
    productId: filters.productId,
    productName: activeFilteredProduct?.name,
  })
  const pageStats = useMemo(
    () => ({
      current: promotions.filter((promotion) => promotion.lifecycleStatus === 'current').length,
      upcoming: promotions.filter((promotion) => promotion.lifecycleStatus === 'upcoming').length,
      inactive: promotions.filter((promotion) => promotion.lifecycleStatus === 'inactive').length,
      linked: promotions.filter((promotion) => Boolean(promotion.productId)).length,
    }),
    [promotions]
  )

  const openCreatePromotion = () => {
    setSelectedPromotion(null)
    setDefaultProductId(filters.productId || seededProductId || null)
    setIsFormOpen(true)
  }

  const clearFilters = () => {
    setFilters({ ...DEFAULT_FILTERS, productId: '' })
    if (searchParams.has('productId') || searchParams.has('create') || searchParams.has('new')) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete('productId')
      nextParams.delete('create')
      nextParams.delete('new')
      setSearchParams(nextParams, { replace: true })
    }
  }

  const openEditPromotion = (promotion: PromotionResponse) => {
    setSelectedPromotion(promotion)
    setDefaultProductId(promotion.productId)
    setIsFormOpen(true)
  }

  const closeForm = () => {
    setIsFormOpen(false)
    setSelectedPromotion(null)
    setDefaultProductId(filters.productId || seededProductId || null)
    if (searchParams.has('create') || searchParams.has('new')) {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete('create')
      nextParams.delete('new')
      setSearchParams(nextParams, { replace: true })
    }
  }

  const handleSubmitPromotion = async (payload: PromotionFormPayload) => {
    await saveMutation.mutateAsync(payload)
  }

  const handleTogglePromotion = async (promotion: PromotionResponse) => {
    await toggleMutation.mutateAsync(promotion)
  }

  const handleCopyWhatsAppCopy = (promotion: PromotionResponse) => {
    const product = promotion.productId ? productMap.get(promotion.productId) : null
    const discount = getPromotionDiscountLabel(promotion)
    const validUntilFormatted = promotion.validUntil ? formatPromotionDate(promotion.validUntil) : null

    const lines = [
      `🔥 *¡Promoción Especial: ${promotion.name}!*`,
      promotion.description ? `\n_${promotion.description}_` : '',
      `\n💥 *Descuento:* ${discount}`,
      product ? `📦 *Producto:* ${product.name} (SKU: ${product.sku})` : '',
      validUntilFormatted ? `⏳ *Válido hasta:* ${validUntilFormatted}` : '',
      `\n📲 _¡Escríbenos para hacer tu pedido ahora y aprovechar esta promoción!_`,
    ].filter(Boolean).join('\n')

    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(lines).catch(() => {})
    }
    toast.success('Texto promocional copiado para WhatsApp')
  }

  const handleDeletePromotion = (promotion: PromotionResponse) => {
    setPromotionToDelete(promotion)
  }

  const canGoPrev = pagination.page > 1
  const canGoNext = pagination.page < pagination.totalPages

  return (
    <div className="space-y-6 pb-8">
      <CatalogNavTabs />
      <section className="overflow-hidden rounded-[2rem] border border-amber-100 bg-gradient-to-br from-amber-50 via-white to-orange-50 shadow-sm">
        <div className="grid gap-6 px-5 py-6 lg:grid-cols-[1.4fr_0.8fr] lg:px-8 lg:py-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">
              <Megaphone className="h-3.5 w-3.5" />
              Ventas
            </div>
            <div>
              <h1 data-tour="promotions-main" className="text-3xl font-black tracking-tight text-slate-900 lg:text-4xl">Promociones</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 lg:text-base">
                Crea descuentos, activa campañas y controla vigencia por producto o por tienda desde una sola vista.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                type="button"
                onClick={openCreatePromotion}
                className="inline-flex items-center gap-2 rounded-full bg-[#075f55] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#064e46]"
              >
                <Plus className="h-4 w-4" />
                Nueva promoción
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => promotionsQuery.refetch()}
                className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-white px-4 py-2.5 text-sm font-semibold text-amber-700 transition-colors hover:bg-amber-50"
              >
                <RefreshCw className="h-4 w-4" />
                Refrescar
              </Button>
              <Link
                to="/app/catalog"
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
              >
                <ArrowLeft className="h-4 w-4" />
                Volver a catálogo
              </Link>
            </div>

            <div className="grid gap-3 sm:grid-cols-4">
              <MetricCard label="Resultados" value={String(pagination.total)} icon={CircleDollarSign} />
              <MetricCard label="Vigentes" value={String(pageStats.current)} icon={ToggleRight} />
              <MetricCard label="Próximas" value={String(pageStats.upcoming)} icon={CalendarIcon} />
              <MetricCard label="Vinculadas" value={String(pageStats.linked)} icon={Package} />
            </div>
          </div>

          <div className="rounded-[1.75rem] border border-white/70 bg-white/80 p-4 shadow-lg backdrop-blur">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Estado de filtros</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {hasActiveFilters ? `${countActivePromotionFilters(filters)} filtro${countActivePromotionFilters(filters) === 1 ? '' : 's'} activo${countActivePromotionFilters(filters) === 1 ? '' : 's'}` : 'Vista general'}
                </p>
              </div>
              <div className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                {pagination.page}/{pagination.totalPages}
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <p className="text-sm text-slate-600">
                Las promociones quedan aisladas por tenant y se pueden desactivar sin perder historial operativo.
              </p>
              <div className="rounded-2xl bg-slate-950 p-4 text-white">
                <p className="text-xs uppercase tracking-[0.2em] text-white/50">Acciones sugeridas</p>
                <ul className="mt-3 space-y-2 text-sm text-white/85">
                  <li className="flex items-start gap-2">
                    <Search className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                    Busca por nombre o descripción
                  </li>
                  <li className="flex items-start gap-2">
                    <Filter className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                    Filtra por vigentes, próximas o inactivas
                  </li>
                  <li className="flex items-start gap-2">
                    <ToggleLeft className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                    Activa o desactiva sin perder trazabilidad
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-2 text-slate-900">
          <Filter className="h-4 w-4 text-[#075f55]" />
          <h2 className="text-lg font-bold">Filtros</h2>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-[1.2fr_0.8fr_0.8fr]">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Buscar</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                className="pl-9"
                value={filters.search ?? ''}
                onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value, page: 1 }))}
                placeholder="Nombre o descripción"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Estado</label>
            <select
              value={filters.state ?? 'all'}
              onChange={(e) => setFilters((current) => ({ ...current, state: e.target.value as PromotionStateFilter, page: 1 }))}
              className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm outline-none transition-colors focus:border-[#075E54] focus:ring-2 focus:ring-[#075E54]/20"
            >
              {getPromotionStateOptions().map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Producto</label>
            <select
              value={filters.productId ?? ''}
              onChange={(e) => setFilters((current) => ({ ...current, productId: e.target.value, page: 1 }))}
              className="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm outline-none transition-colors focus:border-[#075E54] focus:ring-2 focus:ring-[#075E54]/20"
            >
              <option value="">Todos los productos</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} · SKU {product.sku}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-500">
            {pagination.total} promoción{pagination.total === 1 ? '' : 'es'} encontrada{pagination.total === 1 ? '' : 's'}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={clearFilters}
              className="rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            >
              Limpiar filtros
            </Button>
          </div>
        </div>

        {activeFilteredProduct ? (
          <div className="mt-3 flex items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50/80 px-3.5 py-2 text-xs text-amber-900">
            <div className="flex items-center gap-2 truncate">
              <Package className="h-4 w-4 shrink-0 text-amber-700" />
              <span>
                Filtrando promociones para: <strong>{activeFilteredProduct.name}</strong> (SKU: {activeFilteredProduct.sku})
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                setFilters((current) => ({ ...current, productId: '', page: 1 }))
                if (searchParams.has('productId')) {
                  const nextParams = new URLSearchParams(searchParams)
                  nextParams.delete('productId')
                  setSearchParams(nextParams, { replace: true })
                }
              }}
              className="shrink-0 font-semibold text-amber-700 hover:text-amber-900 hover:underline"
            >
              Ver todas
            </button>
          </div>
        ) : null}
      </section>

      {promotionsQuery.isLoading ? (
        <section className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="animate-pulse rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="h-4 w-24 rounded bg-slate-200" />
              <div className="mt-3 h-6 w-3/5 rounded bg-slate-200" />
              <div className="mt-4 h-4 w-4/5 rounded bg-slate-200" />
              <div className="mt-3 h-4 w-2/3 rounded bg-slate-200" />
              <div className="mt-4 flex gap-2">
                <div className="h-8 w-20 rounded bg-slate-200" />
                <div className="h-8 w-20 rounded bg-slate-200" />
                <div className="h-8 w-20 rounded bg-slate-200" />
              </div>
            </div>
          ))}
        </section>
      ) : promotionsQuery.isError ? (
        <section className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-rose-800 shadow-sm">
          <h2 className="text-lg font-bold">No pudimos cargar las promociones</h2>
          <p className="mt-2 text-sm">Revisa la conexión o intenta nuevamente.</p>
          <Button type="button" onClick={() => promotionsQuery.refetch()} className="mt-4 bg-[#075f55] text-white hover:bg-[#064d46]">
            Reintentar
          </Button>
        </section>
      ) : promotions.length === 0 ? (
        <section className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 shadow-sm">
          {emptyState ? (
            <div className="space-y-4 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                <Megaphone className="h-7 w-7" />
              </div>
              <div>
                <h2 className="text-2xl font-black text-slate-900">{emptyState.title}</h2>
                <p className="mt-2 text-sm text-slate-600">{emptyState.description}</p>
              </div>
              <div className="flex flex-wrap justify-center gap-3">
                <Button
                  type="button"
                  onClick={emptyState.primaryAction === 'Limpiar filtros' ? clearFilters : openCreatePromotion}
                  className="bg-[#075f55] text-white hover:bg-[#064d46]"
                >
                  {emptyState.primaryAction}
                </Button>
                {emptyState.secondaryAction === 'Ir a catálogo' ? (
                  <Link to="/app/catalog" className="inline-flex items-center rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    Ir a catálogo
                  </Link>
                ) : (
                  <Button type="button" variant="outline" onClick={clearFilters}>
                    {emptyState.secondaryAction}
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </section>
      ) : (
        <section className="grid gap-4 lg:grid-cols-2">
          {promotions.map((promotion) => {
            const product = promotion.productId ? productMap.get(promotion.productId) : null
            const lifecycleBadge = getPromotionLifecycleBadge(promotion.lifecycleStatus)

            return (
              <article key={promotion.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate text-lg font-bold text-slate-900">{promotion.name}</h3>
                      <span className={badgeClass(lifecycleBadge.tone)}>{lifecycleBadge.label}</span>
                      {product ? (
                        <Link
                          to={`/app/products?search=${encodeURIComponent(product.name)}`}
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 transition-colors"
                          title="Ver producto en inventario"
                        >
                          <Package className="h-3 w-3 text-emerald-600" />
                          {product.name}
                        </Link>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-0.5 text-xs font-semibold text-blue-800">
                          <Store className="h-3 w-3 text-blue-600" />
                          Global
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{getPromotionLifecycleDescription(promotion.lifecycleStatus)}</p>
                  </div>
                  <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
                    {getPromotionDiscountLabel(promotion)}
                  </span>
                </div>

                <div className="mt-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
                    {product?.imageUrl ? (
                      <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" />
                    ) : (
                      <Package className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-900">{getPromotionTargetLabel(product?.name)}</p>
                    <p className="truncate text-xs text-slate-500">
                      {product ? `SKU ${product.sku} · Stock ${product.stock}` : 'Promoción general sin producto vinculado'}
                    </p>
                  </div>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <InfoBox label="Destino" value={getPromotionTargetLabel(product?.name)} />
                  <InfoBox label="Estado" value={promotion.active ? 'Activa' : 'Inactiva'} />
                  <InfoBox label="Inicio" value={formatPromotionDate(promotion.validFrom)} />
                  <InfoBox label="Fin" value={formatPromotionDate(promotion.validUntil)} />
                </div>

                {promotion.description ? <p className="mt-4 text-sm leading-6 text-slate-600">{promotion.description}</p> : null}

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleCopyWhatsAppCopy(promotion)}
                    className="gap-2 border-emerald-200 text-[#075f55] hover:bg-emerald-50"
                    title="Copiar texto con formato para enviar por WhatsApp"
                  >
                    <Copy className="h-4 w-4" />
                    Copiar WhatsApp
                  </Button>
                  <Button type="button" variant="outline" onClick={() => openEditPromotion(promotion)} className="gap-2">
                    <Pencil className="h-4 w-4" />
                    Editar
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleTogglePromotion(promotion)}
                    disabled={toggleMutation.isPending}
                    className="gap-2"
                  >
                    {promotion.active ? <ToggleLeft className="h-4 w-4" /> : <ToggleRight className="h-4 w-4" />}
                    {promotion.active ? 'Desactivar' : 'Activar'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleDeletePromotion(promotion)}
                    disabled={deleteMutation.isPending}
                    className="gap-2 border-rose-200 text-rose-700 hover:bg-rose-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    Desactivar
                  </Button>
                </div>
              </article>
            )
          })}
        </section>
      )}

      {pagination.totalPages > 1 ? (
        <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <p className="text-sm text-slate-600">
            Página <span className="font-semibold text-slate-900">{pagination.page}</span> de{' '}
            <span className="font-semibold text-slate-900">{pagination.totalPages}</span>
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={!canGoPrev}
              onClick={() => setFilters((current) => ({ ...current, page: Math.max(1, (current.page ?? 1) - 1) }))}
            >
              Anterior
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!canGoNext}
              onClick={() => setFilters((current) => ({ ...current, page: Math.min(pagination.totalPages, (current.page ?? 1) + 1) }))}
            >
              Siguiente
            </Button>
          </div>
        </div>
      ) : null}

      <PromotionFormModal
        isOpen={isFormOpen}
        mode={selectedPromotion ? 'edit' : 'create'}
        promotion={selectedPromotion}
        defaultProductId={defaultProductId}
        products={products}
        isSaving={saveMutation.isPending}
        onClose={closeForm}
        onSubmit={handleSubmitPromotion}
      />

      {/* Modal de confirmación para desactivar promoción */}
      <Modal
        isOpen={Boolean(promotionToDelete)}
        onClose={() => !deleteMutation.isPending && setPromotionToDelete(null)}
        title="Desactivar Promoción"
        size="sm"
      >
        <div className="space-y-4">
          <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-bold">¿Confirmas desactivar la promoción?</p>
              <p className="mt-1 text-xs text-amber-800">
                Se desactivará la promoción <strong>"{promotionToDelete?.name}"</strong>. Podrás volver a activarla en cualquier momento con un clic.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-2.5 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPromotionToDelete(null)}
              disabled={deleteMutation.isPending}
              className="rounded-xl"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={async () => {
                if (promotionToDelete) {
                  await deleteMutation.mutateAsync(promotionToDelete)
                  setPromotionToDelete(null)
                }
              }}
              disabled={deleteMutation.isPending}
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-xl shadow-sm"
            >
              {deleteMutation.isPending ? 'Desactivando...' : 'Desactivar promoción'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

function badgeClass(tone: 'emerald' | 'amber' | 'rose' | 'slate') {
  if (tone === 'emerald') return 'rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700'
  if (tone === 'amber') return 'rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700'
  if (tone === 'rose') return 'rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700'
  return 'rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600'
}

function InfoBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-3">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className="mt-1 text-sm font-semibold text-slate-900">{value}</p>
    </div>
  )
}

function MetricCard({
  label,
  value,
  icon: Icon,
}: {
  label: string
  value: string
  icon: ComponentType<{ className?: string }>
}) {
  return (
    <div className="rounded-2xl border border-white/70 bg-white/80 p-4 shadow-sm backdrop-blur">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
          <p className="mt-2 text-2xl font-black text-slate-900">{value}</p>
        </div>
        <div className="rounded-2xl bg-amber-50 p-3 text-amber-700">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  )
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 2v4M16 2v4M3 10h18M5 6h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z" />
    </svg>
  )
}
